// HelpBarButton — the tap trigger for the ⌘K palette (<HelpBarRoot/> in
// HelpBar.tsx), and the event that opens it.
//
// Kept apart from the palette on purpose: every <PageHeader/> carries this
// button (W-14), so it must not drag cmdk, the help index and the speech
// helpers into every page that has a header. The palette imports the event
// name from here, not the other way round.
//
// Two shapes:
//   - `dashboard` — the labelled "Search / Go to ⌘K" pill in the Dashboard
//     header (icon-only below sm).
//   - `header`    — beside the back arrow in every page header, so a touch
//     user with no keyboard and no rail is one tap from any module (X-04).
//     Icon-only below sm; "Go to" beside the icon from sm up.

import { createContext, useContext } from 'react'
import { Search } from 'lucide-react'

export const HELPBAR_OPEN_EVENT = 'kf49:helpbar-open'

/** Opens the ⌘K command palette from anywhere (a header button, a kiosk tap). */
export function openHelpBar() {
  window.dispatchEvent(new CustomEvent(HELPBAR_OPEN_EVENT))
}

/** Does this app mount <HelpBarRoot/>? True for the main app. The admin app
 *  (src/admin/) does not: its navigation is the rail, and the palette's
 *  destinations are main-app routes. With no root listening, the button would
 *  be a control that does nothing, so it renders nothing instead. */
export const HelpBarAvailable = createContext(true)

export function HelpBarButton({ variant = 'dashboard', surface = 'shell' }: {
  variant?: 'dashboard' | 'header'
  /** What a `header` button sits on — the same prop name and idea as
   *  <BackButton/>, which it stands beside. `light` is for a white page
   *  header (SIZA readiness): the shell's gray-800 chip is a dark slab there. */
  surface?: 'shell' | 'light'
}) {
  const available = useContext(HelpBarAvailable)
  if (!available) return null
  if (variant === 'header') {
    return (
      <button
        type="button"
        onClick={openHelpBar}
        data-guide="help.goto"
        title="Search and go to anywhere (⌘K)" aria-label="Search and go to anywhere"
        // -ml-2 pairs it with the back arrow (both are navigation); the 44px
        // boxes still meet the touch floor side by side. From sm it is a
        // bordered chip, like the Dashboard's: a bare "Go to" beside the page
        // title read as one phrase ("Go to  Fuel"). Raw sm: colours because
        // .surface / .hairline-strong are plain CSS, which takes no variant.
        className={`-ml-2 min-w-[44px] min-h-[44px] sm:ml-0 sm:px-3 sm:border inline-flex items-center justify-center gap-1.5 rounded-xl text-sm shrink-0 transition-colors print:hidden ${surface === 'light'
          ? 'sm:border-gray-300 sm:bg-white sm:hover:bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200'
          : 'sm:border-line sm:bg-surface sm:hover:bg-raised text-fg-placeholder hover:text-white light:hover:text-fg hover:bg-surface'}`}>
        <Search size={18} className="sm:w-4 sm:h-4" />
        <span className="hidden sm:inline">Go to</span>
      </button>
    )
  }
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toLowerCase().includes('mac')
  return (
    <button
      type="button"
      onClick={openHelpBar}
      data-guide="help.goto"
      className="rounded-xl surface hover:bg-gray-700 border hairline-strong px-3 py-1.5 coarse:min-h-[44px] coarse:min-w-[44px] text-sm text-gray-400 flex items-center justify-center gap-2 transition-colors"
      title="Search and go to anywhere (⌘K)" aria-label="Search and go to anywhere">
      <Search size={14} />
      {/* UF-08: Home's row has room for the word on a phone (the Add-ons and
          Work offline buttons leave it for those who can use them). */}
      <span className="sm:hidden">Go to</span>
      <span className="hidden sm:inline">Search / Go to</span>
      <kbd className="hidden sm:inline ml-1 text-[10px] surface-sunk border hairline-strong rounded px-1 py-0.5">
        {isMac ? '⌘' : 'Ctrl'}K
      </kbd>
    </button>
  )
}
